import axios from 'axios/index'
import store from './stores'
import router from './router'
import Vue from 'vue'
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';

import { revertLangCode } from "@/services/util.service"

const SNAPPY_API = axios.create({
  baseURL: process.env.VUE_APP_API
})

const _isInvalidToken = (responseData) => {
  if (!responseData)
    return false

  const { errorCode, errorMsg } = responseData || {}
  if (errorCode === 'USR_ERR_0030')
    return true

  // Catch token errors that come with different error codes
  if (typeof errorMsg === 'string' && errorMsg.toLowerCase().includes('invalid authentication token'))
    return true

  return false
}

const _isAuthError = (error) => {
  const responseData = get(error, 'response.data')
  const status = get(error, 'response.status')
  
  if (status === 401 || status === 403) {
    return true
  }
  if (_isInvalidToken(responseData)) {
    return true
  }
  const { errorCode } = responseData || {}
  const authErrorCodes = ['USR_ERR_0030', 'UNAUTHORIZED', 'TOKEN_EXPIRED']
  
  return authErrorCodes.includes(errorCode)
}

const requestInterceptor = {
  success: (config, headers) => {
    const excludedRoutes = ["/lite_menus", "/menu", "/store", "/manifest", "/store_group"];
    const currentApiRoute = config.url.split('?')[0];
    const shouldExclude = excludedRoutes.some((keyword) => {
      return currentApiRoute === keyword || config.url.includes(keyword + "/");
    });
    if (store.getters.isLoggedIn && !shouldExclude)
      config.headers["Authorization"] = store.getters.getAuthToken;

    const sid =
      get(window, "params.sid") ||
      get(router, "history.current.params.sid") ||
      store.getters.getStoreId

    const gid = get(window, 'params.gid') || get(router, 'history.current.params.gid') || store.getters.getStore.groupId

    if (config.headers['storeId'] === 'NULL') {
      delete config.headers.storeId;
    } else {
      if (sid && sid !== 'null' && sid !== 0) {
        config.headers['storeId'] = sid
      } else {
        delete config.headers.storeId;
      }
    }

    if (gid && gid !== 'null' && gid !== 0) {
      config.headers['white-label-group-id'] = config.headers['white-label-group-id'] || gid
    }

    config.headers['app-name'] = 'OWA'
    config.headers['Content-Type'] = 'application/json'
    config.headers['Accept-Language'] = revertLangCode(Vue.prototype.$vuetify.lang.current)

    // Avoid CORS issue by only using this with prod
    if (process.env.VUE_APP_API.includes('https://gosnappy'))
      config.headers['cache-control'] = 'no-cache'
    return config
  },
  error: (error) => {
    return Promise.reject(error)
  }
}

const responseInterceptor = {
  success: (response) => {
    return response
  },
  error: (error) => {
    if (_isAuthError(error) && store.getters.isLoggedIn) {
      store.dispatch('logout')
      window.location.reload()
    }

    return Promise.reject(error)
  }
}

// DEV-11345: identical GETs already in flight share one network request. Startup fires the
// same calls from several places at once (coupons x5, lite_menus x3, the full menu x2...).
// Key = everything that shapes the request after interceptors (URL, params, headers incl.
// auth/storeId/language), so different users/stores/languages never share. Each extra caller
// gets its own deep copy of the data, so one caller mutating it can't affect another.
// ponytail: only collapses concurrent calls; sequential re-fetches need their callers fixed.
const inFlightGets = new Map()
const sendRequest = SNAPPY_API.defaults.adapter
SNAPPY_API.defaults.adapter = (config) => {
  if ((config.method || 'get').toLowerCase() !== 'get') return sendRequest(config)
  const key = JSON.stringify([config.baseURL, config.url, config.params, config.headers])
  const shared = inFlightGets.get(key)
  if (shared) {
    return shared.then(response => Object.assign({}, response, { config, data: cloneDeep(response.data) }))
  }
  const request = sendRequest(config)
  inFlightGets.set(key, request)
  const done = () => inFlightGets.delete(key)
  request.then(done, done)
  return request
}

SNAPPY_API.interceptors.request.use(requestInterceptor.success, requestInterceptor.error)
SNAPPY_API.interceptors.response.use(responseInterceptor.success, responseInterceptor.error)

export const api = SNAPPY_API
